import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  constructor(private toastr: ToastrService) { }
  /**
   * Show success toast message
   */
  success(message: string, title?: string, options?: any): void {
    setTimeout(() => {
      this.toastr.success(message, title, {
        ...options,
        toastClass: 'ngx-toastr custom-toast toast-success-custom',
      });
    });
  }

  /**
   * Show error toast message
   */
  error(message: string, title?: string, options?: any): void {
    setTimeout(() => {
      this.toastr.error(message, title, {
        ...options,
        toastClass: 'ngx-toastr custom-toast toast-error-custom',
        timeOut: 8000, // Longer timeout for errors
      });
    });
  }

  /**
   * Show warning toast message
   */
  warning(message: string, title?: string, options?: any): void {
    setTimeout(() => {
      this.toastr.warning(message, title, {
        ...options,
        toastClass: 'ngx-toastr custom-toast toast-warning-custom',
      });
    });
  }

  /**
   * Show info toast message
   */
  info(message: string, title?: string, options?: any): void {
    setTimeout(() => {
      this.toastr.info(message, title, {
        ...options,
        toastClass: 'ngx-toastr custom-toast toast-info-custom',
      });
    });
  }

  /**
   * Show operation success toast
   */
  operationSuccess(operation: string, entityName?: string): void {
    const message = entityName 
      ? `${entityName} ${operation} successfully`
      : `Operation ${operation} successfully`;
    this.success(message, 'Success');
  }

  /**
   * Show operation error toast
   */
  operationError(operation: string, error?: string, entityName?: string): void {
    const baseMessage = entityName 
      ? `Failed to ${operation} ${entityName}`
      : `Operation failed: ${operation}`;
    const message = error ? `${baseMessage}. ${error}` : baseMessage;
    this.error(message, 'Error');
  }

  /**
   * Show save success toast
   */
  saveSuccess(entityName?: string): void {
    this.operationSuccess('saved', entityName);
  }

  /**
   * Show save error toast
   */
  saveError(error?: string, entityName?: string): void {
    this.operationError('save', error, entityName);
  }

  /**
   * Show delete success toast
   */
  deleteSuccess(entityName?: string): void {
    this.operationSuccess('deleted', entityName);
  }

  /**
   * Show delete error toast
   */
  deleteError(error?: string, entityName?: string): void {
    this.operationError('delete', error, entityName);
  }

  /**
   * Show create success toast
   */
  createSuccess(entityName?: string): void {
    this.operationSuccess('created', entityName);
  }

  /**
   * Show create error toast
   */
  createError(error?: string, entityName?: string): void {
    this.operationError('create', error, entityName);
  }

  /**
   * Show update success toast
   */
  updateSuccess(entityName?: string): void {
    this.operationSuccess('updated', entityName);
  }

  /**
   * Show update error toast
   */
  updateError(error?: string, entityName?: string): void {
    this.operationError('update', error, entityName);
  }

  /**
   * Show validation error toast
   */
  validationError(message: string = 'Please check the form for errors'): void {
    this.error(message, 'Validation Error');
  }

  /**
   * Show loading toast (for long operations)
   */
  loading(message: string = 'Processing...', title: string = 'Please wait'): void {
    this.info(message, title, {
      timeOut: 0, // Don't auto-dismiss
      extendedTimeOut: 0,
      closeButton: true,
      tapToDismiss: false
    });
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toastr.clear();
  }
}
